"use client";

import { type ReactNode, useEffect, useState } from "react";
import { LoginRequiredModal } from "@/components/core";
import { useAuth } from "@/context/auth";

interface WalletGuardProps {
  children: ReactNode;
  /**
   * Server-side verdict, computed in the protected layout from the auth cookie.
   * It is only ever correct for the render that produced it.
   */
  isLoggedIn: boolean;
}

/**
 * Gate for protected routes.
 *
 * The `isLoggedIn` prop comes from a **server component**, so it is a snapshot
 * taken when the page was rendered. Logging out happens on the client and does
 * not re-run that layout — which is why logging out on checkout left the buyer
 * sitting on checkout, still looking at the previous account's data.
 *
 * So the client session is checked too, and either one may close the gate. The
 * client check is deferred until after mount because the persisted auth store
 * starts as logged-out and rehydrates from localStorage; enforcing it during
 * hydration would flash the login modal at every signed-in visitor.
 */
export const WalletGuard = ({ children, isLoggedIn }: WalletGuardProps) => {
  const clientLoggedIn = useAuth((s) => s.isLoggedIn);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => setHydrated(true), []);

  if (!isLoggedIn || (hydrated && !clientLoggedIn)) {
    return <LoginRequiredModal open={true} preventClose={false} />;
  }

  return children;
};
